import {
  LONG_SHIFT_THRESHOLD_HOURS,
  type EarningFlag,
  type TimeSession,
} from '@worklogix/shared';

/**
 * Visible review annotations surfaced on a time session in Hours & Timesheet.
 *
 * Dev-A's shipped `TimeSession` folds clock-skew AND posted-frozen conflicts
 * into ONE optional `needsReview` flag (per its JSDoc: "a late/anomalous punch
 * touched this employee-day but could NOT be applied — e.g. it would rewrite a
 * posted, frozen session, or the clock skew exceeds tolerance"). So there is a
 * single review badge sourced from `needsReview`, plus a separate "posted /
 * frozen" indicator sourced from `postedToNs` (posted sessions are immutable).
 * This module is the only place in web-app that maps those fields to badges.
 *
 * A third, more severe annotation comes from `earningFlag`: the employee has no
 * usable NetSuite earning type for the department these hours would bill to, so the
 * session cannot post at all until payroll fixes the employee's file.
 *
 * A fourth comes from `longShift`: the stretch has run past the threshold. It is DERIVED
 * by the API at read time rather than stored, so for a session that is still open the
 * figure grows between refreshes — which is the point, since the usual cause is a
 * missed clock-out.
 */
export type SessionFlagKind =
  | 'noEarning'
  | 'overlap'
  | 'longShift'
  | 'backToBack'
  | 'needsReview'
  | 'frozen';

/** A single active flag with the copy + severity used to render a badge. */
export interface SessionFlag {
  kind: SessionFlagKind;
  /** Short badge label. */
  label: string;
  /** Longer tooltip/explanation (non-sensitive). */
  description: string;
  /** Maps to a Fluent Badge `color`. */
  severity: 'danger' | 'warning' | 'important';
}

/**
 * Badge for an {@link EarningFlag}, whatever kind of entry it came from.
 *
 * Shared rather than per-surface so worked time, PTO, and every future report page
 * describe the same problem in the same words — a supervisor should not have to
 * learn two vocabularies for one failure.
 */
export function earningFlagBadge(flag: EarningFlag): SessionFlag {
  const noun = flag.kind === 'wage' ? 'earning' : `${flag.kind} earning`;
  return {
    kind: 'noEarning',
    label: flag.status === 'ambiguous' ? `Ambiguous ${noun}` : `No ${noun} type`,
    // The API already phrases this as what payroll must ADD or REMOVE; passing it
    // through beats re-wording it here and drifting from the poster's own reason.
    description: `${flag.reason} (department: ${flag.departmentName})`,
    severity: 'danger',
  };
}

/**
 * Derive the ordered list of active review annotations for a session. The needs
 * review flag (the actionable one) renders first; the frozen indicator second.
 */
export function deriveSessionFlags(session: TimeSession): SessionFlag[] {
  const flags: SessionFlag[] = [];

  // FIRST, and `danger` rather than `warning`: this one is not a "look at this", it
  // is a certainty. These hours WILL be skipped at posting time, because the
  // employee has no usable earning type for the department they would bill to. And
  // it cannot be cleaned up afterwards on the cheap — NetSuite stamps a paycheck's
  // time lines with the employee's HOME department, and correcting one later is
  // refused unless they already hold the target department's earning. Payroll adding
  // the earning BEFORE the run is the only inexpensive fix, which is what this badge
  // is for.
  if (session.earningFlag !== undefined) {
    flags.push(earningFlagBadge(session.earningFlag));
  }

  // A stretch past the threshold (Michael, 2026-09-03). `danger` and ranked ABOVE "needs
  // review" when the employee is STILL CLOCKED IN: that is not "look at this later",
  // it is almost always a forgotten clock-out, and every further hour is wrong pay
  // accruing right now. A CLOSED long shift is a payroll-review matter, so it drops to
  // `warning` and reads as a plain observation — some people really do work 14 hours.
  if (session.longShift !== undefined) {
    const { hours, stillClockedIn } = session.longShift;
    // Already rounded to two decimals by the API; String() drops a trailing .0 on its own.
    const shown = String(hours);
    flags.push({
      kind: 'longShift',
      label: stillClockedIn ? `Still clocked in — ${shown} h` : `Long shift — ${shown} h`,
      description: stillClockedIn
        ? `This employee has been clocked in for ${shown} hours and has not clocked out. ` +
          `This is most likely a missed clock-out — the hours keep accruing until it ` +
          `is corrected.`
        : `This session ran ${shown} hours, past the ${String(LONG_SHIFT_THRESHOLD_HOURS)}-hour ` +
          `threshold. Check it before approving.`,
      severity: stillClockedIn ? 'danger' : 'warning',
    });
  }

  /**
   * OVERLAPPING ENTRIES (Michael, 2026-09-14). `danger`, and ranked directly under the
   * earning flag: two sessions covering the same minutes is always wrong — one of them
   * is a duplicate, and approving both pays those minutes twice.
   */
  if (session.overlapFlag !== undefined) {
    flags.push({
      kind: 'overlap',
      label: 'Overlapping entry',
      description:
        'This session overlaps another entry for the same employee, so the same minutes ' +
        'are recorded twice. One of the two is wrong — resolve it before approving either.',
      severity: 'danger',
    });
  }

  /**
   * CLOCKED OUT AND STRAIGHT BACK IN. `warning`, not `danger`: unlike an overlap the
   * hours are not double-counted, it is two rows where there should be one — usually a
   * double-tap at the kiosk. Worth correcting, not worth stopping payroll for.
   */
  if (session.backToBackFlag !== undefined) {
    const gap = session.backToBackFlag.gapMinutes;
    const howLong =
      gap === 0 ? 'in the same minute' : `${String(gap)} minute${gap === 1 ? '' : 's'} later`;
    flags.push({
      kind: 'backToBack',
      label: 'Back-to-back entry',
      description:
        `This employee clocked out and back in ${howLong}, in the same department — ` +
        'most often a double-tap at the kiosk. Consider merging the two entries.',
      severity: 'warning',
    });
  }

  if (session.needsReview === true) {
    flags.push({
      kind: 'needsReview',
      label: 'Needs review',
      description:
        'A late or anomalous punch (e.g. clock skew beyond tolerance, or a conflict with an already-posted day) touched this employee-day and could not be applied automatically. Review before approving.',
      severity: 'warning',
    });
  }
  if (session.postedToNs) {
    flags.push({
      kind: 'frozen',
      label: 'Posted — frozen',
      description:
        'This session has been posted to NetSuite and is frozen; it cannot be edited and late punches will not rewrite it.',
      severity: 'important',
    });
  }
  return flags;
}

/** True when a session carries any review annotation (used to emphasize the row). */
export function hasAnyFlag(session: TimeSession): boolean {
  return deriveSessionFlags(session).length > 0;
}
